import React from "react";
import { Image, ScrollView } from "~/ui/primitives";
import styled, { css, useTheme } from "~/ui/styled";
import { dismissKeyboardOnPress } from "~utils/keyboard";
import FastImage from "~/ui/FastImage";
import { StartFlex, ColumnFlex, CenterFlex, Text } from "~components/El";

const logo = require("~assets/images/app-intro-logo.png");
const logoUri = Image.resolveAssetSource(logo).uri;

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  logoSize?: number;
  bottomHeight?: string;
  onSubmit?: () => void;
  pageBackground?: string;
  sidePanelOpen?: boolean;
  sidePanel?: React.ReactNode;
}

const Page = styled.div<{ $pageBackground?: string }>`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $pageBackground }) =>
    $pageBackground ?? "linear-gradient(135deg, #07c160 0%, #06ad56 50%, #059a4c 100%)"};
  padding: 24px;
  box-sizing: border-box;
  overflow: auto;
`;

const Stage = styled.div<{ $split: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  max-width: ${({ $split }) =>
    $split ? "min(1200px, calc(100vw - 48px))" : "440px"};
  gap: ${({ $split }) => ($split ? "16px" : "0")};
  transition:
    max-width 0.34s ease,
    gap 0.34s ease;
`;

const cardShifted = css`
  flex: 0 0 auto;
  width: min(440px, calc(100vw - 48px - 336px));
  max-width: 440px;
  min-width: 0;
`;

const Card = styled(ColumnFlex)<{ $shifted: boolean }>`
  width: 100%;
  max-width: 440px;
  min-height: 480px;
  max-height: calc(100vh - 48px);
  height: auto;
  flex-shrink: 0;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  transform: translateX(${({ $shifted }) => ($shifted ? "-8px" : "0")});
  transition:
    transform 0.34s ease,
    width 0.34s ease,
    flex 0.34s ease,
    max-width 0.34s ease;
  ${({ $shifted }) => $shifted && cardShifted}
`;

const SidePanelWrap = styled.div<{ $open: boolean }>`
  flex: ${({ $open }) => ($open ? "1 1 360px" : "0 0 0")};
  width: ${({ $open }) => ($open ? "auto" : "0")};
  min-width: ${({ $open }) => ($open ? "min(320px, calc(100vw - 48px - 440px - 16px))" : "0")};
  max-width: ${({ $open }) =>
    $open ? "min(640px, calc(100vw - 48px - 300px - 16px))" : "0"};
  height: ${({ $open }) => ($open ? "calc(100vh - 48px)" : "0")};
  max-height: calc(100vh - 48px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateX(${({ $open }) => ($open ? "0" : "32px")});
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  overflow: hidden;
  transition:
    flex 0.34s ease,
    width 0.34s ease,
    min-width 0.34s ease,
    opacity 0.3s ease,
    transform 0.34s ease;
`;

const CardHeader = styled(StartFlex)`
  width: 100%;
  padding: 28px 28px 8px;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const Logo = styled(CenterFlex)<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  flex-shrink: 0;
  border-radius: ${({ size }) => size / 4}px;
  background-color: ${({ theme }) => theme.colors.grayBackground};
  overflow: hidden;
`;

const CardBody = styled(ColumnFlex)`
  flex: 1;
  width: 100%;
  min-height: 0;
  padding: 0 28px 28px;
`;

const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  logoSize = 64,
  onSubmit,
  pageBackground,
  sidePanelOpen = false,
  sidePanel,
}) => {
  const theme = useTheme();
  const split = sidePanelOpen && !!sidePanel;

  return (
    <Page
      $pageBackground={pageBackground}
      {...(dismissKeyboardOnPress ? { onClick: dismissKeyboardOnPress } : {})}
    >
      <Stage $split={split}>
        <Card $shifted={split}>
        <CardHeader>
          <Logo size={logoSize}>
            <FastImage
              style={{ height: "100%", width: "100%" }}
              src={logoUri}
              resizeMode={FastImage.resizeMode.stretch}
            />
          </Logo>
          <ColumnFlex>
            <Text
              color={theme.colors.text}
              fontSize={theme.fontSizes["2xl"]}
              fontWeight="600"
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                color={theme.colors.secondaryText}
                fontSize={theme.fontSizes.md}
                mt="6px"
              >
                {subtitle}
              </Text>
            ) : null}
          </ColumnFlex>
        </CardHeader>

        <CardBody>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            style={{ flex: 1, width: "100%" }}
            contentContainerStyle={{ flexGrow: 1 }}
          >
            <form
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                minHeight: 0,
              }}
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit?.();
              }}
            >
              {children}
            </form>
          </ScrollView>
        </CardBody>
      </Card>
      {sidePanel ? (
        <SidePanelWrap $open={split} onClick={(e) => e.stopPropagation()}>
          {sidePanel}
        </SidePanelWrap>
      ) : null}
      </Stage>
    </Page>
  );
};

export default AuthLayout;
